/**
 * Module-contributed `when` context keys (见 03 §5.9 / 21).
 *
 * `when` clauses on activityBar items, menus and keybindings (plus command
 * `enablement`) are evaluated against a flat context ({@link WhenContext}). The
 * platform fills in what it can observe itself (`workbench.activeViewId`,
 * `workbench.online`, …); this contract is how a **module** publishes an axis the
 * platform has no way to know about — e.g. org-menu publishing which kind of
 * space is current, so `org-console` can declare
 * `when: "org-menu.spaceType == personal"` in its manifest.
 *
 * **Push, not a provider — deliberately unlike `registerScopeLayer` /
 * `registerRailPresetLayer`.** Those are pull (`read()` + `onDidChange`) because
 * their consumer lives in the extension host and can call `read()` synchronously.
 * `when` evaluation happens in the **renderer** and has to be synchronous (it
 * runs during render and inside keyboard handlers), so a pull layer would become
 * one async RPC per atom. The workable shape is therefore: the host holds a
 * snapshot, broadcasts it, and each renderer evaluates against its local copy —
 * which is `GateBroker`'s shape (the kernel holds state it does not interpret, a
 * `source` module pushes it, re-readable, broadcast).
 *
 * **Only cross-window state belongs here.** The platform's own keys stay
 * renderer-local because they legitimately differ per window (two windows have
 * different active views). The host snapshot carries module business state,
 * which every window must agree on.
 */
import type { WhenContext } from './when';

/** Cross-process channel for the module-contributed `when` context snapshot. */
export const WHEN_CONTEXT_CHANNEL = 'when-context';

/**
 * EventBus event emitted whenever a module-contributed key changes; the payload
 * is the whole {@link WhenContextChange} snapshot, so a receiver never has to
 * apply deltas in order.
 *
 * Deliberately **not** in `PLATFORM_PUBLIC_EVENTS` (same reasoning as
 * `LAUNCHER_CHANGED_EVENT`): the snapshot names which modules are installed and
 * carries their business state, neither of which a sandboxed surface should see.
 */
export const WHEN_CONTEXT_CHANGED_EVENT = 'platform.whenContext.changed';

/**
 * Namespace the platform's own `when` keys live in (`workbench.ready`,
 * `workbench.activeViewId`, …). Refused to modules by `contextKeyProblem` so a
 * module cannot shadow a platform key: module ids are not otherwise barred from
 * being literally `workbench`, and the namespace rule alone would let that one
 * case through.
 */
export const PLATFORM_CONTEXT_KEY_PREFIX = 'workbench.';

/**
 * What a module may publish as a key's value.
 *
 * Restricted to what `evaluateWhen` can actually compare: it stringifies
 * primitives and yields `''` for everything else, so accepting an object would
 * let a module believe it had set a value while every comparison silently failed.
 * `bigint` is excluded even though `evaluateWhen` handles it — the snapshot
 * crosses a process boundary as JSON, where `bigint` does not survive.
 */
export type WhenContextValue = string | number | boolean;

/** Payload of {@link WHEN_CONTEXT_CHANGED_EVENT}: the full module-key snapshot. */
export interface WhenContextChange {
  context: WhenContext;
}

/**
 * Trusted-renderer RPC for the module-contributed part of the `when` context.
 *
 * Read at boot and on every reconnect rather than relying on the broadcast
 * alone: an `onStartup` module publishes its keys before any renderer connects
 * (same reasoning as `IGateService.getActiveGate`).
 */
export interface IWhenContextService {
  getSnapshot(): Promise<WhenContext>;
}

/**
 * In-process module face for publishing `when` context keys (见 03 §5.9).
 *
 * Only `source` modules see a `ModuleContext` at all (§5.6 信任模型), so a
 * sandboxed/downloaded module cannot reach this. Beyond that it is ungated:
 * a key is confined to the module's own namespace, and publishing your own state
 * is safe — the platform never interprets it, and only a manifest that opted in
 * by naming the key reacts to it.
 */
export interface WhenContextApi {
  /**
   * Publish `key` as `value`, or remove it when `value` is `undefined`.
   *
   * `key` must lie in the calling module's namespace and outside
   * {@link PLATFORM_CONTEXT_KEY_PREFIX}; violations **throw**. Silently ignoring
   * them would surface as "the `when` clause is never true, so the item never
   * appears" — a symptom with nothing to search for.
   *
   * Keys are dropped when the module deactivates, so there is no disposer to
   * push onto `subscriptions`.
   */
  setContextKey(key: string, value: WhenContextValue | undefined): void;
}
